import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
} from "@mui/material";
import FireStoreService from "../services/FireStoreService";

const DETAIL_ORDER_FOR_PRINCIPAL = [
  "Job Description",
  "Last Date",
  "Job Responsibilities",
  "Essential Requirements",
  "Desirable Requirements",
];

const HIDDEN_KEYS = new Set(["id", "Post", "createdAt", "updatedAt"]);

function formatLabel(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

const JobListing = () => {
  const [jobs, setJobs] = useState([]);
  const [position, setPosition] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    fullName: "",
    dob: "",
    currentAddress: "",
    previousAddress: "",
    phoneNumber: "",
    bachelorDegree: "",
    cv: null,
    microsoftOfficeExperience: "",
  });

  const selectedJob =
    position !== null && jobs[position] ? jobs[position] : null;

  const showJobDetails = (index) => {
    if (position === index) {
      setPosition(null);
      setSelectedIndex(null);
      return;
    }

    setPosition(index);
    setSelectedIndex(index);
  };

  const handleApplyNow = () => {
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setFormData((prevData) => ({
      ...prevData,
      cv: file,
    }));
  };

  const handleRadioChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmitForm = async (event) => {
    event.preventDefault();
    const file = formData.cv;

    if (!formData.email || !formData.fullName || !formData.dob || !formData.cv) {
      alert("Please fill out all required fields.");
      return;
    }

    const reader = new FileReader();

    reader.onloadend = async () => {
      const base64Data = reader.result.split("base64,")[1];
      const fileData = {
        base64: base64Data,
        type: file.type,
        name: file.name,
      };

      try {
        const uploadResponse = await fetch(
          "https://script.google.com/macros/s/AKfycbxuZWv4l3SUy6GLphaqs7rqvg4pBB5zMdxI0KnXdxpnLP2YJAGReC1cdGvdoTNyZIw/exec",
          {
            method: "POST",
            body: JSON.stringify(fileData),
          },
        );

        const uploadResult = await uploadResponse.text();

        if (!uploadResponse.ok) {
          alert("Upload failed!");
          return;
        }

        const payload = {
          Email: formData.email,
          FullName: formData.fullName,
          DOB: formData.dob,
          CurrentAddress: formData.currentAddress,
          PreviousAddress: formData.previousAddress,
          PhoneNumber: formData.phoneNumber,
          BachelorDegree: formData.bachelorDegree,
          MicrosoftOfficeExperience: formData.microsoftOfficeExperience,
          CV: uploadResult,
        };

        const submitResponse = await fetch(
          "https://script.google.com/macros/s/AKfycbzu7k9paGxEGZh9fDyZxnBo3TUGuk5R_mB_R6O8uPwwbeH19DMUYLH7uqRvUza3DE-R/exec",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: new URLSearchParams(payload).toString(),
          },
        );

        const submitResult = await submitResponse.text();

        if (submitResponse.ok) {
          alert("Form submitted successfully!");
          console.log(submitResult);
          handleCloseForm();
          return;
        }

        alert("Failed to submit the form. Please try again.");
        console.error("Form submission failed:", submitResult);
      } catch (error) {
        console.error("Error submitting the form:", error);
        alert(
          "An error occurred while submitting the form. Please check the console for more details.",
        );
      }
    };

    reader.readAsDataURL(file);
  };

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const jobsData = await FireStoreService.fetchData("JobsList");
        setJobs(jobsData);

        if (jobsData.length > 0) {
          setPosition(0);
          setSelectedIndex(0);
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
      }
    };

    fetchJobs();
  }, []);

  const orderObject = (obj, order) => {
    const orderedObj = {};
    order.forEach((key) => {
      if (obj[key]) {
        orderedObj[key] = obj[key];
      }
    });
    return orderedObj;
  };

  function TraceJson({ obj }) {
    let orderedObj = obj;

    if (obj && obj.Post === "Principal") {
      orderedObj = orderObject(obj, DETAIL_ORDER_FOR_PRINCIPAL);
    }

    const renderKeyValue = (data) => {
      if (data === null || data === undefined) {
        return (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        );
      }

      return Object.entries(data).map(([key, value]) => {
        if (HIDDEN_KEYS.has(key)) {
          return null;
        }

        if (value === null || value === undefined || value === "") {
          return null;
        }

        if (typeof value === "object" && !Array.isArray(value)) {
          return (
            <Box
              key={key}
              sx={{
                mt: 2,
                border: "1px solid #e2e8f0",
                borderRadius: "14px",
                p: 2,
                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#1e3a8a",
                  mb: 1,
                  fontSize: "0.95rem",
                }}
              >
                {formatLabel(key)}
              </Typography>
              <Box sx={{ pl: 1 }}>{renderKeyValue(value)}</Box>
            </Box>
          );
        }

        if (Array.isArray(value)) {
          return (
            <Box
              key={key}
              sx={{
                mt: 2,
                border: "1px solid #e2e8f0",
                borderRadius: "14px",
                p: 2,
                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#1e3a8a",
                  mb: 1,
                  fontSize: "0.95rem",
                }}
              >
                {formatLabel(key)}
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {value.map((item, index) => (
                  <Typography
                    component="li"
                    key={`${key}-${index}`}
                    sx={{ color: "#334155", py: 0.3, fontSize: "0.95rem" }}
                  >
                    {String(item)}
                  </Typography>
                ))}
              </Box>
            </Box>
          );
        }

        return (
          <Box
            key={key}
            sx={{
              mt: 1.5,
              borderRadius: "12px",
              p: 1.5,
              border: "1px solid #e2e8f0",
              bgcolor: "#f8fafc",
            }}
          >
            <Typography
              sx={{ fontWeight: 700, color: "#1e3a8a", fontSize: "0.88rem" }}
            >
              {formatLabel(key)}
            </Typography>
            <Typography sx={{ mt: 0.4, color: "#334155", fontSize: "0.95rem" }}>
              {String(value)}
            </Typography>
          </Box>
        );
      });
    };

    return <Box>{renderKeyValue(orderedObj)}</Box>;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              border: "1px solid #dbeafe",
              borderRadius: "20px",
              p: 2,
              bgcolor: "#f8fafc",
              maxHeight: { md: "72vh" },
              overflowY: "auto",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.18em",
                color: "#1d4ed8",
              }}
            >
              Open Roles
            </Typography>
            <Typography
              sx={{ mt: 1, fontWeight: 700, fontSize: "1.2rem", color: "#0f172a" }}
            >
              Available Positions
            </Typography>
            <Typography sx={{ mt: 0.6, color: "#64748b", fontSize: "0.9rem" }}>
              Select a role to view complete requirements and apply.
            </Typography>

            <Grid container spacing={1.5} sx={{ mt: 1 }}>
              {jobs.map((job, index) => (
                <Grid item xs={12} key={job.id || `${job.Post}-${index}`}>
                  <Card
                    onClick={() => showJobDetails(index)}
                    sx={{
                      borderRadius: "16px",
                      border:
                        selectedIndex === index
                          ? "1.5px solid #2563eb"
                          : "1px solid #e2e8f0",
                      bgcolor: selectedIndex === index ? "#eff6ff" : "#ffffff",
                      boxShadow:
                        selectedIndex === index
                          ? "0 10px 25px rgba(37, 99, 235, 0.18)"
                          : "0 2px 10px rgba(15, 23, 42, 0.06)",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.1)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Typography
                        sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}
                      >
                        {job.Post}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 1,
                          color: "#475569",
                          fontSize: "0.88rem",
                          lineHeight: 1.45,
                        }}
                      >
                        {job.Experience || "Experience details will be updated."}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          <Box
            sx={{
              border: "1px solid #dbeafe",
              borderRadius: "20px",
              p: { xs: 2, sm: 2.5 },
              bgcolor: "#ffffff",
              minHeight: { md: "72vh" },
              maxHeight: { md: "72vh" },
              overflowY: "auto",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.18em",
                color: "#1d4ed8",
              }}
            >
              Job Details
            </Typography>

            {selectedJob ? (
              <Typography
                sx={{ mt: 1, fontWeight: 800, color: "#0f172a", fontSize: "1.5rem" }}
              >
                {selectedJob.Post}
              </Typography>
            ) : (
              <Typography
                sx={{
                  mt: 1.2,
                  color: "#64748b",
                  fontStyle: "italic",
                  fontSize: "0.95rem",
                }}
              >
                Select a job from the left panel to view details.
              </Typography>
            )}

            <Box sx={{ mt: 2 }}>
              <TraceJson obj={selectedJob} />
            </Box>

            {selectedJob ? (
              <>
                <Typography
                  sx={{
                    mt: 2.5,
                    fontWeight: 700,
                    fontStyle: "italic",
                    color: "#dc2626",
                    fontSize: "0.92rem",
                  }}
                >
                  Application will only be accepted through online submission.
                </Typography>

                {selectedJob.Post !== "Principal" ? (
                  <Button
                    variant="contained"
                    onClick={handleApplyNow}
                    sx={{
                      mt: 2,
                      borderRadius: "12px",
                      px: 3,
                      py: 1.1,
                      textTransform: "none",
                      fontWeight: 700,
                      bgcolor: "#2563eb",
                      "&:hover": { bgcolor: "#1d4ed8" },
                    }}
                  >
                    Apply Now
                  </Button>
                ) : (
                  <a
                    href="https://docs.google.com/forms/d/e/1FAIpQLScl0ZmvB8v1_MtTcnd1R2cYzWZeoGDMvc6Ejb8uXZQV7ftjNA/viewform"
                    style={{ color: "inherit", textDecoration: "none" }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      variant="contained"
                      sx={{
                        mt: 2,
                        borderRadius: "12px",
                        px: 3,
                        py: 1.1,
                        textTransform: "none",
                        fontWeight: 700,
                        bgcolor: "#2563eb",
                        "&:hover": { bgcolor: "#1d4ed8" },
                      }}
                    >
                      Apply Now
                    </Button>
                  </a>
                )}
              </>
            ) : null}
          </Box>
        </Grid>
      </Grid>

      <Dialog open={formOpen} onClose={handleCloseForm} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            bgcolor: "#eff6ff",
            borderBottom: "1px solid #dbeafe",
            color: "#0f172a",
            fontWeight: 700,
          }}
        >
          Job Application Form
        </DialogTitle>
        <DialogContent sx={{ pt: "20px !important" }}>
          <Box
            component="form"
            sx={{ display: "flex", flexDirection: "column", gap: "14px" }}
          >
            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Full Name"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Date of Birth"
              name="dob"
              type="date"
              value={formData.dob}
              onChange={handleInputChange}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Current Address"
              name="currentAddress"
              value={formData.currentAddress}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              label="Previous Address"
              name="previousAddress"
              value={formData.previousAddress}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              label="Phone Number"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              fullWidth
            />

            <FormControl component="fieldset">
              <Typography variant="body2" sx={{ color: "#334155", mb: 0.5 }}>
                Bachelor Degree
              </Typography>
              <RadioGroup
                name="bachelorDegree"
                value={formData.bachelorDegree}
                onChange={handleRadioChange}
                row
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset">
              <Typography variant="body2" sx={{ color: "#334155", mb: 0.5 }}>
                Microsoft Office Experience
              </Typography>
              <RadioGroup
                name="microsoftOfficeExperience"
                value={formData.microsoftOfficeExperience}
                onChange={handleRadioChange}
                row
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>

            <Button
              variant="outlined"
              component="label"
              sx={{
                mt: 0.5,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                borderColor: "#93c5fd",
                color: "#1d4ed8",
              }}
            >
              Upload CV
              <input
                type="file"
                name="cv"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                hidden
                required
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={handleCloseForm}
            sx={{ textTransform: "none", fontWeight: 600, color: "#334155" }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            onClick={handleSubmitForm}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              bgcolor: "#2563eb",
              "&:hover": { bgcolor: "#1d4ed8" },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobListing;
